import { sendNewRelicPageAction } from '../../analytics/newRelicUtils';
import globalLogger from '../../logger';
import stopwatchSingleton from './stopwatchSingleton';

// utility functions for framerate analysis
const sum = (a: number, b: number) => a + b;
const mean = (arr: Array<number>) => arr.reduce(sum, 0) / arr.length;

const channel = globalLogger.createChannel('stopwatch');

type RecordingAnnotation = undefined | Record<string, string | number | boolean | undefined>;

export type Recording = {
    startDuration: number;
    finishDuration: number;
    meanDuration: number;
    stdDev: number;
    frames: Array<number>;
    name: string;
    annotation: RecordingAnnotation;
};

/**
 * A class for measuring framerate across the duration of a user action.
 * Note that this is not designed for measuring the execution time of a function; use
 * analyticsTimingService for that.
 */
export class Stopwatch {
    last: number = performance.now();
    frames: Array<number> | null = null;
    startDuration: number = 0;
    finishDuration: number = 0;
    annotation: RecordingAnnotation;

    name: string;
    recordRate: number;

    /**
     * Create a new stopwatch.
     * @param name A name to refer to the recorded behaviour by
     * @param recordRate The chance that a particular instance of the action should be recorded
     *             (eg 0.01 to only record 1/100 actions, 1.0 to record all actions, etc)
     */
    constructor(name: string, recordRate: number = 1.0) {
        this.name = name;
        this.recordRate = recordRate;
    }

    private tick() {
        const now = performance.now();
        const dt = now - this.last;
        this.last = now;
        return dt;
    }

    private shouldRecord() {
        // if the singleton is enabled, we're in debugging mode - we want to record everything please
        if (stopwatchSingleton.isEnabled()) return true;

        // otherwise respect the sample rate - low sample rate = low chance of recording
        return Math.random() < this.recordRate;
    }

    /**
     * Mark the beginning of a user action. This should be called as close to the instant
     * of the initial action as possible.
     * @param annotation An object containing additional data relevant to the action
     *                   (eg { draggedItemCount: 39 })
     */
    start(annotation: RecordingAnnotation) {
        if (!this.shouldRecord()) return;

        this.frames = [];
        this.startDuration = 0;
        this.finishDuration = 0;
        this.last = performance.now();
        this.annotation = annotation;
    }

    onFrame = () => {
        // finish will clear this.frames, ending the recording
        if (!this.frames) return;

        // stop measuring after ~10 seconds @ 60fps
        // (just to prevent things getting out of control)
        if (this.frames.length > 600) return;

        requestAnimationFrame(this.onFrame);

        const duration = this.tick();
        this.frames.push(duration);
    };

    /**
     * Mark a frame boundary. This should be called every frame that the user action is
     * happening. It doesn't really matter when in the frame it gets called, as long as it
     * is at the same point every frame.
     *
     * Internally, the stopwatch just uses its own calls to requestAnimationFrame to monitor
     * the frame rate (this is to account for the fact that ElementDragLayerStateManager only
     * updates if the user moved the cursor that frame, resulting in inaccurate framerate stats)
     */
    markFrame() {
        // if we've already recorded the startDuration, leave things to the animation system
        if (this.startDuration) return;

        // record the time since start as the start duration
        const duration = this.tick();
        this.startDuration = duration;

        // and then kick things over to rAF to record framerate until finish is called
        requestAnimationFrame(this.onFrame);
    }

    /**
     * Mark the end of the action. Ideally this should be called once the action has fully
     * resolved, after any cleanup code has finished running etc.
     */
    finish() {
        if (!this.frames) return;

        this.finishDuration = this.tick();

        const meanDuration = mean(this.frames);
        const squaredDeviations = this.frames.map((x) => Math.pow(x - meanDuration, 2));
        const stdDev = Math.sqrt(mean(squaredDeviations));

        const results: Recording = {
            name: this.name,
            annotation: this.annotation,
            frames: this.frames,
            startDuration: this.startDuration,
            finishDuration: this.finishDuration,
            meanDuration,
            stdDev,
        };

        channel.info(`${this.name} finished`, results);

        // reset frames to null to indicate no active recording
        this.frames = null;

        this.report(results);

        // send to singleton for local profiling
        stopwatchSingleton.addRecording(results);
    }

    report(recording: Recording) {
        const {
            // not interested in sending to NR
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            frames,

            // metrics
            startDuration,
            finishDuration,
            meanDuration,
            stdDev,

            // additional info
            name,
            annotation,
            ...data
        } = recording;

        const extraData = { ...data, ...annotation };

        // send as a batch of independent actions so that the most relevant metric is on the "duration" key
        // this means they'll get looped in to NR's aggregation
        sendNewRelicPageAction(`stopwatch.${name}.start`, { duration: startDuration, ...extraData });
        sendNewRelicPageAction(`stopwatch.${name}.end`, { duration: finishDuration, ...extraData });
        sendNewRelicPageAction(`stopwatch.${name}.meanFrame`, { duration: meanDuration, stdDev, ...extraData });
    }
}
